import {
  DATASET_TRIGGER_CRITERIA,
  DATASET_TRIGGER_RUN_MODES,
  type DatasetTriggerCriterion,
  type DatasetTriggerRunMode,
} from '@cotera/api';
import { Assert } from '@cotera/utilities';

/**
 * What a dataset trigger watches for.
 *
 * Identical for every dataset kind. How candidate rows are produced — full
 * artifact scan, operation delta, watermark range — is the dataset's business,
 * so kind never appears here or in the UI built on top of it.
 *
 * The values themselves live in `@cotera/api` so the wire schema and this
 * module cannot drift; the labels and predicates below are the domain layer.
 */
export { DATASET_TRIGGER_CRITERIA, type DatasetTriggerCriterion };

export const DATASET_TRIGGER_CRITERION_LABELS: Record<
  DatasetTriggerCriterion,
  string
> = {
  new_row: 'A new row is added',
  record_changed: 'Values change',
  record_first_meets_conditions: 'A row first matches conditions',
  row_deleted: 'A row is deleted',
};

export const DATASET_TRIGGER_CRITERION_DESCRIPTIONS: Record<
  DatasetTriggerCriterion,
  string
> = {
  new_row: 'Runs once the first time a row is seen.',
  record_changed: 'Runs whenever the watched fields on a row change value.',
  record_first_meets_conditions:
    'Runs the first time a row satisfies your conditions, and not again.',
  row_deleted: 'Runs when a row that was previously seen disappears.',
};

/**
 * Whether the trigger acts on what it detects, or waits to be asked.
 *
 * Deliberately not a fifth criterion: the criterion decides *which* rows a run
 * is about and still has to be answered for a manual run, because the dataset
 * grid replays rows through it. See {@link DATASET_TRIGGER_RUN_MODES}.
 */
export { DATASET_TRIGGER_RUN_MODES, type DatasetTriggerRunMode };

export const DATASET_TRIGGER_RUN_MODE_LABELS: Record<
  DatasetTriggerRunMode,
  string
> = {
  automatic: 'Automatically',
  manual: 'Only when I run it',
};

export const DATASET_TRIGGER_RUN_MODE_DESCRIPTIONS: Record<
  DatasetTriggerRunMode,
  string
> = {
  automatic: 'Runs on its own whenever the dataset matches.',
  manual: 'Only runs when you start it from the dataset.',
};

export const isDatasetTriggerCriterion = (
  value: unknown
): value is DatasetTriggerCriterion =>
  typeof value === 'string' &&
  (DATASET_TRIGGER_CRITERIA as readonly string[]).includes(value);

export const isDatasetTriggerRunMode = (
  value: unknown
): value is DatasetTriggerRunMode =>
  typeof value === 'string' &&
  (DATASET_TRIGGER_RUN_MODES as readonly string[]).includes(value);

/**
 * Whether the criterion decides by comparing values rather than by row identity
 * alone.
 *
 * Only these carry `trackFields` and a fingerprint — for the rest, a key either
 * appears or it does not, and there is nothing to watch. The trigger editor uses
 * this to decide whether to offer a field picker at all.
 */
export const criterionTracksValues = (
  criterion: DatasetTriggerCriterion
): boolean => criterion === 'record_changed';

/**
 * Whether the criterion is answered by diffing observed-state against a
 * *complete* key set. A partial scan (a watermark range, a resumed page) must
 * never be read as "everything else was deleted".
 */
export const criterionNeedsCompleteKeySet = (
  criterion: DatasetTriggerCriterion
): boolean => criterion === 'row_deleted';

/**
 * How the change is described to the workflow that runs. Distinct from the
 * criterion because `new_row` and `record_first_meets_conditions` both report a
 * row appearing to the trigger for the first time.
 */
export type DatasetRowChangeType = 'row_added' | 'row_changed' | 'row_deleted';

export const changeTypeForCriterion = (
  criterion: DatasetTriggerCriterion
): DatasetRowChangeType => {
  switch (criterion) {
    case 'new_row':
    case 'record_first_meets_conditions':
      return 'row_added';
    case 'record_changed':
      return 'row_changed';
    case 'row_deleted':
      return 'row_deleted';
    default:
      return Assert.unreachable(criterion);
  }
};
